import { getApiBaseUrl, API_TIMEOUT, DEFAULT_HEADERS } from '@/constants/api';
import { createApiError, logError } from '@/utils/error';
import { getToken } from './tokenStorage';

/**
 * Опции для API запроса
 */
type RequestOptions = {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  headers?: Record<string, string>;
  body?: unknown;
  timeout?: number;
  skipAuth?: boolean; // Пропустить добавление токена авторизации
};

/**
 * Базовый API клиент с обработкой ошибок
 */
class ApiClient {
  private baseUrl: string;

  constructor() {
    this.baseUrl = getApiBaseUrl();
  }

  /**
   * Выполняет запрос к API
   * @param endpoint - Endpoint для запроса
   * @param options - Опции запроса
   * @returns Promise с данными ответа
   */
  async request<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
    const {
      method = 'GET',
      headers = {},
      body,
      timeout = API_TIMEOUT,
      skipAuth = false,
    } = options;

    const url = `${this.baseUrl}${endpoint}`;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    // Добавляем токен авторизации, если он есть и не пропущен
    const requestHeaders: Record<string, string> = {
      ...DEFAULT_HEADERS,
      ...headers,
    };

    if (!skipAuth) {
      const token = getToken();
      if (token) {
        requestHeaders.Authorization = `Bearer ${token}`;
      }
    }

    try {
      const response = await fetch(url, {
        method,
        headers: requestHeaders,
        body: body ? JSON.stringify(body) : undefined,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw await createApiError(response);
      }

      // Если ответ пустой, возвращаем пустой объект
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        return {} as T;
      }

      const data = (await response.json()) as T;
      return data;
    } catch (error) {
      clearTimeout(timeoutId);

      // Обработка ошибки отмены запроса (таймаут)
      if (error instanceof Error && error.name === 'AbortError') {
        logError(error, 'API Request Timeout');
        throw new Error(
          `Превышено время ожидания ответа от сервера (${timeout / 1000} секунд). Проверьте подключение к интернету.`,
        );
      }

      // Если это уже наша ошибка, пробрасываем её
      if (error instanceof Error && error.name === 'ApiErrorException') {
        throw error;
      }

      // Обработка сетевых ошибок
      if (error instanceof TypeError && (error.message.includes('fetch') || error.message.includes('network'))) {
        logError(error, 'API Network Error');
        throw new Error('Ошибка подключения к серверу. Проверьте интернет-соединение.');
      }

      logError(error, 'API Request Error');
      throw error;
    }
  }

  /**
   * GET запрос
   */
  async get<T>(endpoint: string, options?: Omit<RequestOptions, 'method' | 'body'>): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'GET' });
  }

  /**
   * POST запрос
   */
  async post<T>(endpoint: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'POST', body });
  }

  /**
   * PUT запрос
   */
  async put<T>(endpoint: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'PUT', body });
  }

  /**
   * DELETE запрос
   */
  async delete<T>(endpoint: string, options?: Omit<RequestOptions, 'method' | 'body'>): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'DELETE' });
  }
}

/**
 * Экземпляр API клиента
 */
export const apiClient = new ApiClient();
